// The surface content region. Every dashboard surface renders its OWN toolbar-led chrome (screen-chrome.tsx
// — the prototype's sticky toolbar + screen body), so this view is a thin dispatcher: it mounts the live
// component inside the error boundary, labelled by the toolbar's h1 (id="surface-heading").
//
// The Work SECTION (WS-J) keeps its own self-contained chrome (it predates the toolbar-led frame and has its
// own in-section router/headers), so it renders inside the legacy padded wrapper unchanged. A surface with
// no live component renders the prototype's honest empty state (.empty) — it fabricates NO data (P-SELF).

import type { DashboardRole, SurfaceDefinition } from './surfaces.ts';
import { surfaceComponentFor } from '../surfaces/registry.tsx';
import { SurfaceErrorBoundary } from './SurfaceErrorBoundary.tsx';
import { Icon } from './Icon.tsx';

/** The prototype's honest empty state for a surface with no live render yet (app.js .empty). */
function EmptySurface({ surface }: { readonly surface: SurfaceDefinition }): React.JSX.Element {
  return (
    <div className="screen-body">
      <div className="empty">
        <div className="ico">
          <Icon name="compass" size={48} />
        </div>
        <h2 id="surface-heading">{surface.label}</h2>
        <p>
          This surface is part of the full Ritona build — its owning-module read is wired in a later
          build stage. Nothing here is fabricated.
        </p>
      </div>
    </div>
  );
}

export interface SurfaceViewProps {
  readonly surface: SurfaceDefinition;
  /** The shell-resolved role, passed to the live component (only the Work section reads it — G-180). */
  readonly role: DashboardRole;
}

/** Render one surface's content region — the live component (own chrome), or the honest empty state. */
export function SurfaceView({ surface, role }: SurfaceViewProps): React.JSX.Element {
  const Live = surfaceComponentFor(surface.id);

  // The Work section (WS-J) keeps its pre-toolbar chrome: a padded, width-capped wrapper + its own headers.
  if (surface.id === 'work' && Live !== undefined) {
    return (
      <section aria-label={surface.label} data-surface={surface.id} className="screen-body">
        <SurfaceErrorBoundary key={surface.id}>
          <Live role={role} />
        </SurfaceErrorBoundary>
      </section>
    );
  }

  return (
    <section aria-labelledby="surface-heading" data-surface={surface.id}>
      {Live !== undefined ? (
        <SurfaceErrorBoundary key={surface.id}>
          <Live role={role} />
        </SurfaceErrorBoundary>
      ) : (
        <EmptySurface surface={surface} />
      )}
    </section>
  );
}
